import { schema } from 'normalizr';
import { RSAA } from 'redux-api-middleware';
import get from 'lodash/get';
import { handleResponse, handleError, buildQueryString } from '../utils';

export const FETCH_DIMENSIONLISTS_REQUEST = 'FETCH_DIMENSIONLISTS_REQUEST';
export const FETCH_DIMENSIONLISTS_SUCCESS = 'FETCH_DIMENSIONLISTS_SUCCESS';
export const FETCH_DIMENSIONLISTS_UPDATE_SUCCESS = 'FETCH_DIMENSIONLISTS_UPDATE_SUCCESS';
export const FETCH_DIMENSIONLISTS_FAILURE = 'FETCH_DIMENSIONLISTS_FAILURE';

export const FETCH_DIMENSIONLISTOVERVIEW_REQUEST = 'FETCH_DIMENSIONLISTOVERVIEW_REQUEST';
export const FETCH_DIMENSIONLISTOVERVIEW_SUCCESS = 'FETCH_DIMENSIONLISTOVERVIEW_SUCCESS';
export const FETCH_DIMENSIONLISTOVERVIEW_FAILURE = 'FETCH_DIMENSIONLISTOVERVIEW_FAILURE';

export const FETCH_DIMENSIONLIST_DETAILS_REQUEST = 'FETCH_DIMENSIONLIST_DETAILS_REQUEST';
export const FETCH_DIMENSIONLIST_DETAILS_SUCCESS = 'FETCH_DIMENSIONLIST_DETAILS_SUCCESS';
export const FETCH_DIMENSIONLIST_DETAILS_FAILURE = 'FETCH_DIMENSIONLIST_DETAILS_FAILURE';

export const FETCH_DIMENSIONLISTACTIVITY_REQUEST = 'FETCH_DIMENSIONLISTACTIVITY_REQUEST';
export const FETCH_DIMENSIONLISTACTIVITY_SUCCESS = 'FETCH_DIMENSIONLISTACTIVITY_SUCCESS';
export const FETCH_DIMENSIONLISTACTIVITY_FAILURE = 'FETCH_DIMENSIONLISTACTIVITY_FAILURE';

export const CREATE_DIMENSIONLIST_REQUEST = 'CREATE_DIMENSIONLIST_REQUEST';
export const CREATE_DIMENSIONLIST_SUCCESS = 'CREATE_DIMENSIONLIST_SUCCESS';
export const CREATE_DIMENSIONLIST_FAILURE = 'CREATE_DIMENSIONLIST_FAILURE';

export const EDIT_DIMENSIONLIST_REQUEST = 'EDIT_DIMENSIONLIST_REQUEST';
export const EDIT_DIMENSIONLIST_SUCCESS = 'EDIT_DIMENSIONLIST_SUCCESS';
export const EDIT_DIMENSIONLIST_FAILURE = 'EDIT_DIMENSIONLIST_FAILURE';

export const DELETE_DIMENSIONLIST_REQUEST = 'DELETE_DIMENSIONLIST_REQUEST';
export const DELETE_DIMENSIONLIST_SUCCESS = 'DELETE_DIMENSIONLIST_SUCCESS';
export const DELETE_DIMENSIONLIST_FAILURE = 'DELETE_DIMENSIONLIST_FAILURE';

export const APPROVE_DIMENSIONLIST_REQUEST = 'APPROVE_DIMENSIONLIST_REQUEST';
export const APPROVE_DIMENSIONLIST_SUCCESS = 'APPROVE_DIMENSIONLIST_SUCCESS';
export const APPROVE_DIMENSIONLIST_FAILURE = 'APPROVE_DIMENSIONLIST_FAILURE';

export const REJECT_DIMENSIONLIST_REQUEST = 'REJECT_DIMENSIONLIST_REQUEST';
export const REJECT_DIMENSIONLIST_SUCCESS = 'REJECT_DIMENSIONLIST_SUCCESS';
export const REJECT_DIMENSIONLIST_FAILURE = 'REJECT_DIMENSIONLIST_FAILURE';

export const DISCARD_DIMENSIONLIST_REQUEST = 'DISCARD_DIMENSIONLIST_REQUEST';
export const DISCARD_DIMENSIONLIST_SUCCESS = 'DISCARD_DIMENSIONLIST_SUCCESS';
export const DISCARD_DIMENSIONLIST_FAILURE = 'DISCARD_DIMENSIONLIST_FAILURE';

export const FETCH_DIMENSIONLISTCHANGELOG_REQUEST = 'FETCH_DIMENSIONLISTCHANGELOG_REQUEST';
export const FETCH_DIMENSIONLISTCHANGELOG_SUCCESS = 'FETCH_DIMENSIONLISTCHANGELOG_SUCCESS';
export const FETCH_DIMENSIONLISTCHANGELOG_FAILURE = 'FETCH_DIMENSIONLISTCHANGELOG_FAILURE';

export const dimensionListSchema = new schema.Entity(
  'dimensionLists',
  {},
  {
    idAttribute: 'listId',
  },
);

export const dimensionListDetailsSchema = new schema.Entity(
  'dimensionListDetails',
  {},
  {
    idAttribute: 'listId',
  },
);

export const activitySchema = new schema.Entity('activity', {}, { idAttribute: '_id' });

export const changelogSchema = new schema.Entity('listChangelog', {}, { idAttribute: 'listId' });

/**
 * Fetch all dimensionLists
 *
 * @param {String} listType - DimensionList type.
 * @param {String} searchCriteria - Search criteria.
 * @param {Object} [options] - Options for fetching.
 * @param {Boolean} [options.useCache=true] - If `true`, cache won't be checked & the request will be made.
 * @return {Object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchDimensionLists = (
  { listType, searchCriteria, start = 0, count = 20 } = {},
  options = { useCache: false },
) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionLists?${buildQueryString({
      listType,
      searchCriteria,
      start,
      count,
    })}`,
    method: 'GET',
    bailout: store => options.useCache && get(store, `entities.dimensionLists`, false),
    types: [
      { type: FETCH_DIMENSIONLISTS_REQUEST },
      {
        type: start === 0 ? FETCH_DIMENSIONLISTS_SUCCESS : FETCH_DIMENSIONLISTS_UPDATE_SUCCESS,
        payload: (action, state, res) => handleResponse([dimensionListSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      {
        type: FETCH_DIMENSIONLISTS_FAILURE,
        payload: handleError,
      },
    ],
  },
});

/**
 * Fetch single dimensionList
 *
 * @param {string} listId - DimensionList listId.
 * @param {object} [options] - Options for fetching.
 * @param {boolean} [options.useCache=true] - If `true`, cache won't be checked & the request will be made.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchDimensionListOverview = (listId, options = { useCache: true }) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionLists/${listId}`,
    method: 'GET',
    bailout: store => options.useCache && get(store, `entities.dimensionLists.${listId}`, false),
    types: [
      { type: FETCH_DIMENSIONLISTOVERVIEW_REQUEST },
      {
        type: FETCH_DIMENSIONLISTOVERVIEW_SUCCESS,
        payload: (action, state, res) => handleResponse(dimensionListSchema, action, state, res),
        meta: { listId },
      },
      {
        type: FETCH_DIMENSIONLISTOVERVIEW_FAILURE,
        payload: handleError,
        meta: { listId },
      },
    ],
  },
});

/** Fetch contract activity.
 *
 * @param {string} [listId] - Dimension list Id.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchDimensionListActivity = listId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionlists/${listId}/activity`,
    method: 'GET',
    types: [
      { type: FETCH_DIMENSIONLISTACTIVITY_REQUEST },
      {
        type: FETCH_DIMENSIONLISTACTIVITY_SUCCESS,
        payload: (action, state, res) => handleResponse([activitySchema], action, state, res),
        meta: { listId },
      },
      {
        type: FETCH_DIMENSIONLISTACTIVITY_FAILURE,
        payload: handleError,
      },
    ],
  },
});

/* Fetch single dimensionList details
 *
 * @param {string} address - DimensionList address.
 * @param {object} [options] - Options for fetching.
 * @param {boolean} [options.useCache=true] - If `true`, cache won't be checked & the request will be made.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchDimensionListDetails = (listId, options = { useCache: true }) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionLists/${listId}?${buildQueryString({
      flattenList: true,
    })}`,
    method: 'GET',
    bailout: store =>
      options.useCache && get(store, `entities.dimensionListDetails.${listId}`, false),
    types: [
      { type: FETCH_DIMENSIONLIST_DETAILS_REQUEST },
      {
        type: FETCH_DIMENSIONLIST_DETAILS_SUCCESS,
        payload: (action, state, res) =>
          handleResponse(dimensionListDetailsSchema, action, state, res),
        meta: { listId },
      },
      {
        type: FETCH_DIMENSIONLIST_DETAILS_FAILURE,
        payload: handleError,
        meta: { listId },
      },
    ],
  },
});

//
export const createDimensionList = values => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionlists`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_DIMENSIONLIST_REQUEST },
      {
        type: CREATE_DIMENSIONLIST_SUCCESS,
        payload: (action, state, res) => handleResponse(dimensionListSchema, action, state, res),
      },
      {
        type: CREATE_DIMENSIONLIST_FAILURE,
        payload: handleError,
      },
    ],
  },
});

export const editDimensionList = values => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionlists`,
    method: 'PUT',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_DIMENSIONLIST_REQUEST },
      {
        type: EDIT_DIMENSIONLIST_SUCCESS,
        payload: (action, state, res) => handleResponse(dimensionListSchema, action, state, res),
      },
      {
        type: EDIT_DIMENSIONLIST_FAILURE,
        payload: handleError,
      },
    ],
  },
});

export const deleteDimensionList = listId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionlists/${listId}`,
    method: 'DELETE',
    body: JSON.stringify({ listId }),
    types: [
      { type: DELETE_DIMENSIONLIST_REQUEST },
      { type: DELETE_DIMENSIONLIST_SUCCESS },
      { type: DELETE_DIMENSIONLIST_FAILURE, payload: handleError },
    ],
  },
});

/* Approve dimensionList edits
 *
 * @param {string} list id - unique dimension list id.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const approveDimensionListEdits = (listId, values) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionlists/${listId}/approve`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: APPROVE_DIMENSIONLIST_REQUEST },
      {
        type: APPROVE_DIMENSIONLIST_SUCCESS,
        payload: (action, state, res) => handleResponse(dimensionListSchema, action, state, res),
      },
      {
        type: APPROVE_DIMENSIONLIST_FAILURE,
        payload: handleError,
      },
    ],
  },
});

/* Reject dimensionList edits
 *
 * @param {string} list id - unique dimension list id.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const rejectDimensionListEdits = (listId, values) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionlists/${listId}/reject`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: REJECT_DIMENSIONLIST_REQUEST },
      {
        type: REJECT_DIMENSIONLIST_SUCCESS,
        payload: (action, state, res) => handleResponse(dimensionListSchema, action, state, res),
      },
      {
        type: REJECT_DIMENSIONLIST_FAILURE,
        payload: handleError,
      },
    ],
  },
});

/* Discard dimensionList edits
 *
 * @param {string} list id - unique dimension list id.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const discardDimensionListEdits = (listId, values) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionlists/${listId}/discard`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: DISCARD_DIMENSIONLIST_REQUEST },
      {
        type: DISCARD_DIMENSIONLIST_SUCCESS,
        payload: (action, state, res) => handleResponse(dimensionListSchema, action, state, res),
      },
      {
        type: DISCARD_DIMENSIONLIST_FAILURE,
        payload: handleError,
      },
    ],
  },
});

/**
 * Fetch change log of a dimensionList
 *
 * @param {string} listId - DimensionList listId.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchDimensionListChangelog = listId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/dimensionLists/${listId}/changelog`,
    method: 'GET',
    types: [
      { type: FETCH_DIMENSIONLISTCHANGELOG_REQUEST },
      {
        type: FETCH_DIMENSIONLISTCHANGELOG_SUCCESS,
        payload: (action, state, res) => handleResponse(changelogSchema, action, state, res),
        meta: { listId },
      },
      {
        type: FETCH_DIMENSIONLISTCHANGELOG_FAILURE,
        payload: handleError,
        meta: { listId },
      },
    ],
  },
});
